import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { EyeIcon, PencilSquareIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { mergeOfferLines } from "./utils/offer-line-merge";
import {
  fetchOfferCustomerPresentation,
  generateOfferLinesFromCalculation,
  getPdfOffer,
} from "../../../api/offer";
import { formatMoney } from "./offer-customer-lines";
import OfferQuickLines from "./offer-quick-lines";
import { createOfferSaveQueue, registerOfferSave } from "./utils/offer-save-queue";
import OfferPrintLinesPreview from "./offer-print-lines-preview";
import OfferDocumentLivePreview from "./offer-document-live-preview";
import OfferPresentationRulesDialog from "./offer-presentation-rules-dialog";
import {
  getOfferAsync,
  updateOfferAsync,
  generateOfferLinesFromCalculationAsync,
} from "../../../slices/offer";
import { resolveOfferActionErrorMessage } from "./utils";
import { TWclassNames } from "../../utils/Div";
import { readOfferGroupId } from "./utils/offer-calc-item-groups";
import { useIsDevOrSuperAdmin } from "../../../utils/useIsDevOrSuperAdmin";
import "./offer-workspace.css";
import {
  DEFAULT_OFFER_LINES_VIEW_MODE,
  OFFER_LINES_VIEW_MODES,
  loadOfferLinesViewMode,
  saveOfferLinesViewMode,
} from "./utils/offer-lines-view";
import {
  isDocumentOfferLayout,
  resolveOfferLayout,
} from "./utils/offer-layout";
import {
  isFirmCalculationTemplate,
  pageRootCalculations,
} from "./utils/offer-overview-scope";
import {
  overviewRootCalculationIds,
  prepareCustomerLineItems,
} from "./utils/prepare-customer-line-items";

const ACCENT = "#1463C7";
const LEGACY_EDIT_KEY = "promang.offer.linesLegacyEdit.";

function loadLegacyEditEnabled(offerId) {
  if (offerId == null || typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(`${LEGACY_EDIT_KEY}${offerId}`) === "1";
  } catch {
    return false;
  }
}

function saveLegacyEditEnabled(offerId, enabled) {
  if (offerId == null || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(`${LEGACY_EDIT_KEY}${offerId}`, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function mapCalcItemToCustomerRow(item, rowOrder) {
  const type = item?.item_type || item?.type || "product";
  const isGroup = type === "group" || item?.group_row === true;
  return {
    title: item?.title || "",
    description: item?.description || "",
    quantity: isGroup ? null : item?.quantity ?? 1,
    sales_price: isGroup ? 0 : parseFloat(item?.sales_price || 0),
    discount: parseFloat(item?.discount || 0),
    surcharge: parseFloat(item?.surcharge || 0),
    unit_id: item?.unit_id || item?.unit?.id || null,
    unit: item?.unit || null,
    item_type: type,
    group_row: isGroup,
    visible_to_customer: true,
    row_order: rowOrder,
    product_id: item?.product_id || item?.product?.id || null,
    offer_group_id: readOfferGroupId(item),
    draggableId: `gen-${rowOrder}-${Date.now()}`,
  };
}

/**
 * Local fallback when POST generate_lines_from_calculation is not available.
 * Builds customer rows from offer.calculations[].items and nested calculation items.
 */
export function buildCustomerLinesFromOffer(offer) {
  const rows = [];
  let order = 0;

  const appendItems = (sourceItems) => {
    if (!Array.isArray(sourceItems)) return;
    sourceItems.forEach((item) => {
      rows.push(mapCalcItemToCustomerRow(item, order++));
      const nestedCalc = item?.nested_calculation;
      const nested =
        nestedCalc && !isFirmCalculationTemplate(nestedCalc)
          ? nestedCalc.items
          : null;
      if (Array.isArray(nested) && nested.length) {
        appendItems(nested);
      }
    });
  };

  const calculations = pageRootCalculations(offer?.calculations);
  if (calculations.length) {
    calculations.forEach((calc) => {
      rows.push({
        title: calc?.title || "",
        description: calc?.description || "",
        quantity: null,
        sales_price: 0,
        discount: 0,
        surcharge: 0,
        item_type: "group",
        group_row: true,
        visible_to_customer: true,
        row_order: order++,
        calculation_id: calc?.id ?? null,
        draggableId: `gen-group-${order}-${Date.now()}`,
      });
      appendItems(calc?.items || []);
    });
    return rows;
  }

  // Fallback: items that embed a calculation tree
  const offerItems = offer?.items || [];
  offerItems.forEach((item) => {
    if (item?.calculation?.items?.length) {
      rows.push({
        title: item.title || item.calculation.title || "",
        description: item.description || item.calculation.description || "",
        quantity: null,
        sales_price: 0,
        discount: 0,
        surcharge: 0,
        item_type: "group",
        group_row: true,
        visible_to_customer: true,
        row_order: order++,
        draggableId: `gen-group-${order}-${Date.now()}`,
      });
      appendItems(item.calculation.items);
    }
  });

  return rows;
}

/**
 * Kundevisning — preview av kundebilde generert fra kalkyle (ADR-005).
 * Default er print-preview; manuell quick-edit er midlertidig fallback.
 */
export default function OfferLinesTab({ offer, onPrint }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showDevHints = useIsDevOrSuperAdmin();
  const [items, setItems] = useState(offer?.items || []);
  const [generating, setGenerating] = useState(false);
  // ADR-005: kundevisning (preview) er default — ikke parallell linje-editor
  const [printPreview, setPrintPreview] = useState(true);
  const printLayout = resolveOfferLayout(offer);
  const showDocumentPrint = isDocumentOfferLayout(offer);
  const [outgoingPdfUrl, setOutgoingPdfUrl] = useState("");
  const [outgoingPdfLoading, setOutgoingPdfLoading] = useState(false);
  const [outgoingPdfError, setOutgoingPdfError] = useState(null);
  const outgoingPdfRequestRef = useRef(0);
  const [viewMode, setViewMode] = useState(DEFAULT_OFFER_LINES_VIEW_MODE);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [legacyEditEnabled, setLegacyEditEnabled] = useState(false);
  const [presentationItems, setPresentationItems] = useState(null);

  const [saveState, setSaveState] = useState("saved");
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [generationPreview, setGenerationPreview] = useState([]);
  const currentItems = useRef(items);
  const itemsVersion = useRef(offer?.items_version);
  const baselineItems = useRef(offer?.items || []);
  const [conflict, setConflict] = useState(null);
  const [mergeChoices, setMergeChoices] = useState({});
  const persistRef = useRef(null);
  const queue = useMemo(() => createOfferSaveQueue((next) => persistRef.current(next), setSaveState), [offer?.id]);
  useEffect(() => registerOfferSave(offer?.id, queue), [offer?.id, queue]);
  useEffect(() => {
    if (!queue.isDirty()) { setItems(offer?.items || []); currentItems.current = offer?.items || []; itemsVersion.current = offer?.items_version; baselineItems.current = offer?.items || []; }
  }, [offer?.id, offer?.items, queue]);
  useEffect(() => {
    const guard = (event) => { if (queue.isDirty()) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [queue]);
  const changeItems = (next) => {
    setItems(next);
    currentItems.current = next;
    queue.schedule(next);
  };

  useEffect(() => {
    setViewMode(loadOfferLinesViewMode(offer?.id));
    setLegacyEditEnabled(loadLegacyEditEnabled(offer?.id));
  }, [offer?.id]);

  // ADR-005: kundebilde direkte fra kalkyle + presentasjonsregler.
  // Fingerprint includes updated_at so Edit-calc → tab switch always refetches
  // after getOffer, not only when calculations array identity changes.
  useEffect(() => {
    if (!offer?.id) return undefined;
    let cancelled = false;
    (async () => {
      const data = await fetchOfferCustomerPresentation(offer.id);
      if (cancelled || data?.missing || data?.error) return;
      if (Array.isArray(data?.items)) setPresentationItems(data.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [offer?.id, offer?.updated_at, offer?.subtotal_amount, offer?.presentation_epoch, offer?.presentation_rules, offer?.calculations]);

  const refreshOutgoingPdf = useCallback(async () => {
    if (!offer?.id || !showDocumentPrint) return;
    const requestId = ++outgoingPdfRequestRef.current;
    setOutgoingPdfLoading(true);
    setOutgoingPdfError(null);
    try {
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: printLayout },
      });
      if (requestId !== outgoingPdfRequestRef.current) return;
      if (!response?.pdf_url) {
        setOutgoingPdfError(
          resolveOfferActionErrorMessage(
            t,
            response,
            "offers.documentBuilder.preview.pdf_failed"
          )
        );
        return;
      }
      setOutgoingPdfUrl(response.pdf_url);
    } catch (err) {
      if (requestId !== outgoingPdfRequestRef.current) return;
      setOutgoingPdfError(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    } finally {
      if (requestId === outgoingPdfRequestRef.current) {
        setOutgoingPdfLoading(false);
      }
    }
  }, [offer?.id, printLayout, showDocumentPrint, t]);

  useEffect(() => {
    if (!offer?.id || !showDocumentPrint) {
      setOutgoingPdfUrl("");
      setOutgoingPdfError(null);
      return undefined;
    }
    refreshOutgoingPdf();
    return () => {
      outgoingPdfRequestRef.current += 1;
    };
  }, [
    offer?.id,
    offer?.updated_at,
    offer?.layout,
    showDocumentPrint,
    refreshOutgoingPdf,
  ]);

  // Never soft-generate from stale offer_items when live calc exists — prefer
  // customer_presentation. Only materialize snapshot when presentation is empty
  // AND calc has content (incl. nested orphans healed by API).
  useEffect(() => {
    if (!offer?.id) return;
    const hasCalc = (offer?.calculations || []).length > 0;
    if (!hasCalc || generating) return;
    if (presentationItems?.length) return;
    const calcHasLines = (offer?.calculations || []).some(
      (calc) =>
        (calc?.items || []).length > 0 ||
        (calc?.nested_calculations || []).length > 0
    );
    if (!calcHasLines) return;
    let cancelled = false;
    (async () => {
      setGenerating(true);
      try {
        const result = await dispatch(
          generateOfferLinesFromCalculationAsync(offer.id)
        );
        if (cancelled) return;
        if (generateOfferLinesFromCalculationAsync.fulfilled.match(result)) {
          const nextItems =
            result.payload?.items || result.payload?.offer?.items || [];
          if (nextItems.length) {
            setItems(nextItems);
            currentItems.current = nextItems;
            baselineItems.current = nextItems;
            itemsVersion.current =
              result.payload?.items_version ||
              result.payload?.offer?.items_version ||
              itemsVersion.current;
          }
          const data = await fetchOfferCustomerPresentation(offer.id);
          if (!cancelled && Array.isArray(data?.items)) {
            setPresentationItems(data.items);
          }
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, offer?.calculations, presentationItems?.length]);

  // Prefer live presentation; never fall back to stale offer_items snapshot
  // when a kalkyle exists (T4: offer_items still showed «Calculation 1.0»).
  const previewItems = useMemo(() => {
    const source = presentationItems?.length
      ? presentationItems
      : (offer?.calculations || []).length
        ? presentationItems || []
        : items;
    return prepareCustomerLineItems(source, {
      offer,
      presentationRules: offer?.presentation_rules,
      alreadyPresented: Boolean(presentationItems?.length),
    });
  }, [presentationItems, items, offer]);

  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
      saveOfferLinesViewMode(offer?.id, mode);
    },
    [offer?.id]
  );

  persistRef.current = async (nextItems) => {
    const isBlankDraft = (row) =>
      !row.id &&
      !row.delete &&
      !String(row.title || row.description || "").trim();
    const toPersist = nextItems.filter((row) => !isBlankDraft(row));
    if (
      toPersist.some(
        (row) => !row.delete && !String(row.title || row.description || "").trim()
      )
    ) {
      throw new Error("description_required");
    }
    const result = await dispatch(
      updateOfferAsync({ id: offer.id, items: toPersist, items_version: itemsVersion.current })
    );
    if (updateOfferAsync.rejected.match(result)) {
      const payload = result.payload;
      if (payload?.status === 409 || payload?.response?.status === 409 || JSON.stringify(payload || {}).includes("offer_lines_changed_by_another_user")) {
        const latest = await dispatch(getOfferAsync(offer.id));
        const remote = latest.payload?.offer || latest.payload;
        if (Array.isArray(remote?.items)) {
          setMergeChoices({});
          setConflict({ base: baselineItems.current, local: currentItems.current, remote: remote.items, version: remote.items_version });
        }
      }
      throw new Error("save_failed");
    }
    const saved = result.payload?.offer || result.payload;
    itemsVersion.current = saved?.items_version || itemsVersion.current;
    baselineItems.current = saved?.items || baselineItems.current;
    if (currentItems.current === nextItems && Array.isArray(saved?.items)) {
      const blankDrafts = nextItems.filter(
        (row) =>
          !row.id &&
          !row.delete &&
          !String(row.title || row.description || "").trim()
      );
      currentItems.current = [...saved.items, ...blankDrafts];
      setItems(currentItems.current);
    }
  };

  const previewGeneration = async () => {
    try {
      await queue.flush();
      const response = await generateOfferLinesFromCalculation(offer.id, { preview: true });
      if (!Array.isArray(response?.changes)) throw new Error("preview_failed");
      setGenerationPreview(response.changes);
      setConfirmGenerate(true);
    } catch { toast.error(t("offers.tabs.generate_from_calculation_failed")); }
  };

  const handleGenerate = useCallback(async () => {
    if (!offer?.id || generating) return;
    setGenerating(true);
    setConfirmGenerate(false);

    try {
      await queue.flush();
      const result = await dispatch(
        generateOfferLinesFromCalculationAsync(offer.id)
      );

      if (generateOfferLinesFromCalculationAsync.fulfilled.match(result)) {
        const payload = result.payload;
        const nextItems = payload?.items || payload?.offer?.items || [];
        if (!nextItems.length) {
          toast.error(t("offers.tabs.generate_from_calculation_empty"));
        } else {
          setItems(nextItems);
          toast.success(t("offers.tabs.generate_from_calculation_success"));
          dispatch(getOfferAsync(offer.id));
        }
        return;
      }

      toast.error(resolveOfferActionErrorMessage(t, result.payload, "offers.tabs.generate_from_calculation_failed"));
    } catch {
      toast.error(t("offers.workspace.save_error"));
    } finally {
      setGenerating(false);
    }
  }, [dispatch, offer, generating, t, queue]);

  const mergeResult = conflict ? mergeOfferLines(conflict.base, conflict.local, conflict.remote, mergeChoices) : null;

  if (!offer?.id) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
        <p className="text-sm font-medium text-slate-700">
          {t("offers.tabs.lines")}
        </p>
        <p className="max-w-md text-sm text-slate-500">
          {t("offers.tabs.no_offer")}
        </p>
      </div>
    );
  }

  const currency = offer?.currency || "NOK";
  const groups = offer?.groups || [];
  const viewModeOptions = [
    {
      value: OFFER_LINES_VIEW_MODES.KAPITTEL,
      label: t("offers.tabs.lines_view.kapittel"),
    },
    {
      value: OFFER_LINES_VIEW_MODES.FLAT,
      label: t("offers.tabs.lines_view.flat"),
    },
    {
      value: OFFER_LINES_VIEW_MODES.KUNDEGRUPPER,
      label: t("offers.tabs.lines_view.kundegrupper"),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <PMDialog open={Boolean(conflict)} onClose={() => {}} title="offers.workspace.conflict" size="lg" closeOnOutsideClick={false}>
        <div className="offer-conflict">
          <p>{t("offers.workspace.conflict_hint")}</p>
          {(mergeResult?.conflicts || []).map((entry) => (
            <label key={entry.id} className="offer-conflict-row">
              <span className="offer-conflict-label">
                {entry.title} · {t(`offers.workspace.${entry.field}`, { defaultValue: entry.field })}
              </span>
              <select
                value={mergeChoices[entry.id] || ""}
                onChange={(event) => setMergeChoices({ ...mergeChoices, [entry.id]: event.target.value })}
              >
                <option value="">—</option>
                <option value="local">
                  {t("offers.workspace.mine")}: {String(entry.local ?? "—")}
                </option>
                <option value="remote">
                  {t("offers.workspace.theirs")}: {String(entry.remote ?? "—")}
                </option>
              </select>
            </label>
          ))}
          {mergeResult?.conflicts?.length ? (
            <p className="offer-conflict-hint">{t("offers.workspace.conflict_resolve")}</p>
          ) : null}
          <div className="offer-conflict-actions">
            <button
              type="button"
              onClick={() => {
                dispatch(getOfferAsync(offer.id));
                setConflict(null);
                setMergeChoices({});
              }}
            >
              {t("offers.workspace.conflict_use_server")}
            </button>
            <button
              type="button"
              className="offer-workspace-primary"
              disabled={Boolean(mergeResult?.conflicts.length)}
              onClick={() => {
                itemsVersion.current = conflict.version;
                baselineItems.current = conflict.remote;
                changeItems(mergeResult.items);
                setConflict(null);
                setMergeChoices({});
                queue.flush().catch(() => {});
              }}
            >
              {t("offers.workspace.merge")}
            </button>
          </div>
        </div>
      </PMDialog>
      <div
        className={[
          "offer-save-status",
          saveState === "error" ? "is-error" : "",
          saveState === "pending" || saveState === "saving" ? "is-busy" : "",
          saveState === "saved" ? "is-saved" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="status"
        aria-live="polite"
      >
        <span>{t(`offers.workspace.save_${saveState}`, { defaultValue: t("offers.workspace.save_idle") })}</span>
        {saveState === "error" ? (
          <button type="button" onClick={() => queue.flush().catch(() => {})}>
            {t("offers.workspace.retry")}
          </button>
        ) : null}
        {confirmGenerate ? (
          <div className="offer-regen-alert" role="alert">
            <p>{t("offers.workspace.regenerate_hint")}</p>
            <ul>
              {generationPreview.map((row, index) => (
                <li key={index}>
                  {row.title}:{" "}
                  {row.old_price != null
                    ? `${formatMoney(row.old_price, offer.currency)} → `
                    : "+ "}
                  {formatMoney(row.new_price, offer.currency)}
                </li>
              ))}
            </ul>
            <div className="offer-regen-actions">
              <button type="button" className="offer-workspace-primary" onClick={handleGenerate}>
                {t("offers.workspace.apply")}
              </button>
              <button type="button" onClick={() => setConfirmGenerate(false)}>
                {t("offers.workspace.cancel")}
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <div className="mb-0 flex shrink-0 flex-col gap-3 border-b border-slate-200 bg-white px-5 py-2.5 pl-7 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[12.5px] text-slate-500">
            {printPreview
              ? showDocumentPrint
                ? t("offers.printLinesPreview.mode_hint_document")
                : t("offers.printLinesPreview.mode_hint")
              : showDevHints
                ? t("offers.tabs.lines_customer_hint")
                : t("offers.tabs.lines_customer_hint_public")}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {t("offers.tabs.lines_view.hint")}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
            <span>{t("offers.tabs.lines_view.label")}</span>
            <select
              value={viewMode}
              onChange={(e) => handleViewModeChange(e.target.value)}
              className="offer-select !min-h-[32px] !min-w-[8rem] !py-1 !text-xs"
              aria-label={t("offers.tabs.lines_view.label")}
            >
              {viewModeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div
            className="inline-flex rounded-[7px] border border-slate-200 bg-slate-50 p-0.5"
            role="group"
            aria-label={t("offers.printLinesPreview.toggle")}
          >
            <button
              type="button"
              onClick={() => setPrintPreview(false)}
              disabled={!legacyEditEnabled}
              className={TWclassNames(
                "inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-xs font-semibold transition-colors",
                !printPreview
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700",
                !legacyEditEnabled ? "cursor-not-allowed opacity-40" : ""
              )}
              aria-pressed={!printPreview}
              title={
                legacyEditEnabled
                  ? undefined
                  : t("offers.tabs.legacy_edit_locked_hint")
              }
            >
              <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t("offers.printLinesPreview.edit_mode")}
            </button>
            <button
              type="button"
              onClick={() => setPrintPreview(true)}
              className={TWclassNames(
                "inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-xs font-semibold transition-colors",
                printPreview
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              )}
              style={
                printPreview
                  ? { color: ACCENT, boxShadow: `inset 0 0 0 1px ${ACCENT}33` }
                  : undefined
              }
              aria-pressed={printPreview}
            >
              <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t("offers.printLinesPreview.toggle")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setPresentationOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-[7px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
          >
            {t("offers.presentation.open_button")}
          </button>
          {typeof onPrint === "function" ? (
            <button
              type="button"
              onClick={() => onPrint(printLayout)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-[7px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
            >
              <PrinterIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t("offers.banner.print_current", {
                layout: t(`offers.workspace.layout_${printLayout}`),
              })}
            </button>
          ) : null}
          <details className="relative">
            <summary className="cursor-pointer list-none rounded-[7px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">
              {t("offers.tabs.advanced_menu")}
            </summary>
            <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
              <label className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={legacyEditEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setLegacyEditEnabled(enabled);
                    saveLegacyEditEnabled(offer?.id, enabled);
                    if (!enabled) setPrintPreview(true);
                  }}
                />
                {t("offers.tabs.enable_legacy_edit")}
              </label>
              <button
                type="button"
                onClick={previewGeneration}
                disabled={generating}
                className="mt-1 w-full rounded-md px-2 py-1.5 text-left text-xs font-semibold text-white disabled:bg-slate-200"
                style={generating ? undefined : { background: ACCENT }}
              >
                {t("offers.tabs.update_snapshot")}
              </button>
            </div>
          </details>
        </div>
      </div>

      {printPreview ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          {showDocumentPrint ? (
            <div className="min-h-[320px] min-w-0 flex-1 overflow-hidden border-b border-slate-200 lg:min-h-0 lg:border-b-0 lg:border-r">
              <OfferDocumentLivePreview
                pdfUrl={outgoingPdfUrl}
                loading={outgoingPdfLoading}
                error={outgoingPdfError}
                onRefresh={refreshOutgoingPdf}
              />
            </div>
          ) : null}
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            <OfferPrintLinesPreview
              items={previewItems}
              groups={groups}
              viewMode={viewMode}
              presentationRules={null}
              rootCalculationIds={overviewRootCalculationIds(offer)}
              currency={currency}
              className="offer-print-lines-preview flex h-full min-h-0 flex-col overflow-hidden bg-white"
            />
          </div>
        </div>
      ) : legacyEditEnabled ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <OfferQuickLines
            items={items}
            currency={currency}
            onChange={changeItems}
          />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-500">
          {t("offers.tabs.legacy_edit_locked_hint")}
        </div>
      )}

      <OfferPresentationRulesDialog
        open={presentationOpen}
        onClose={() => setPresentationOpen(false)}
        offer={offer}
        onSaved={async () => {
          await dispatch(getOfferAsync(offer.id));
          const data = await fetchOfferCustomerPresentation(offer.id);
          if (Array.isArray(data?.items)) setPresentationItems(data.items);
        }}
      />
    </div>
  );
}
